// CDV-024 / TECHNICAL.md §4.13.7: lift the chat-send pipeline out of
// ChatPanel so it stays alive when the sidebar is collapsed.
//
// The Form block (rendered inside the sala) needs to send a follow-up
// chat message through the same conversation as the typed sidebar
// input. ChatPanel only mounts when the sidebar is open, so its local
// `sendMessage` would be unreachable from the form. This hook owns the
// streaming / pending / history state and is mounted at AppShell level
// via <ChatConversationProvider>; both ChatSidebar and FormBlockView
// consume it through context.
//
// Zonoo: Assistant Conversation Behavior -- this hook also owns:
//  - fresh-visit / inactivity detection, so reopening the app doesn't
//    silently resurrect a days-old conversation (see isFreshVisit).
//  - the sidebar's draft text (moved here from ChatPanel's own local
//    state so it survives the panel closing/reopening and a page
//    refresh, not just remounts).
//  - a fix for a late (in-flight) response bleeding into a DIFFERENT
//    conversation after the user switches scope or starts a new chat
//    mid-stream (see conversationIdRef below).
//  - interrupted-reply detection (isInterrupted) and a Retry action.

import { apiFetch } from '@/api/client'
import { type ChatPageContext, streamChat } from '@/api/mutations'
import { chatHistoryQuery, queryKeys } from '@/api/queries'
import { ChatHistoryResponse, type ChatMessage } from '@/api/schemas'
import { useAuth } from '@/lib/auth'
import { uuidv4 } from '@/lib/uuid'
import { type ViewIntent, extractViewIntent } from '@/lib/viewIntent'
import { type UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type ReactNode,
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useChatPageContext } from './useChatPageContext'
import { t } from '@/lib/i18n'

const STORAGE_KEY = 'cc_chat_conv_id'
const DRAFT_KEY = 'cc_chat_draft'
const SESSION_SEEN_KEY = 'cc_chat_session_seen'
const LAST_ACTIVE_KEY = 'cc_chat_last_active_at'

// Item 4 of the Assistant Conversation Behavior spec: "returning after
// inactivity" resets to a fresh conversation once 30 minutes have
// passed since the last message/interaction, even within the same
// browser tab (e.g. the panel was left closed in a background tab, or
// the laptop slept).
const INACTIVITY_RESET_MS = 30 * 60 * 1000

// WP-polish (brief §7 "never mix project conversations"): scope the
// persisted conversation id by project so switching sites doesn't carry
// over another project's message history. Portfolio (no site_id) keeps
// using the legacy unscoped key so existing portfolio-level threads
// aren't orphaned by this change.
function scopeIdFor(pageContext: ChatPageContext): string {
  return pageContext.scope === 'portfolio' ? 'portfolio' : pageContext.site_id
}

function storageKeyFor(pageContext: ChatPageContext, userId: string): string {
  const scopeId = scopeIdFor(pageContext)
  // 2026-09-20 bugfix: scope by user id too. Storage for this key is
  // per-tab (sessionStorage, see item 5 "separate tabs" below) but
  // still outlives a client-side logout/login within that same tab --
  // without the user id here, a second user logging in on the same tab
  // (e.g. an admin testing a new client account) would reuse the
  // previous user's stored conversation id and get a bare "you don't
  // have access to this conversation" from the server instead of a
  // fresh thread.
  return `${STORAGE_KEY}:${userId}:${scopeId}`
}

function draftKeyFor(pageContext: ChatPageContext, userId: string): string {
  // Deliberately keyed by SCOPE, not by conversationId: a draft in
  // progress is "what I'm about to say about this project", independent
  // of which conversation thread it ends up landing in. This also means
  // "New chat" and the fresh-visit/inactivity reset below (which mint a
  // new conversationId) never silently discard an unsent draft.
  return `${DRAFT_KEY}:${userId}:${scopeIdFor(pageContext)}`
}

function readDraft(key: string): string {
  try {
    return window.sessionStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

function writeDraft(key: string, text: string): void {
  try {
    if (text) {
      window.sessionStorage.setItem(key, text)
    } else {
      window.sessionStorage.removeItem(key)
    }
  } catch {
    // ignore -- private mode / storage disabled
  }
}

function sessionSeenKey(userId: string): string {
  return `${SESSION_SEEN_KEY}:${userId}`
}

function lastActiveKey(userId: string): string {
  return `${LAST_ACTIVE_KEY}:${userId}`
}

/** Call whenever the user is actively using the Assistant (sending a
 *  message, opening the panel): marks this browser tab/window as "seen"
 *  for the rest of its lifetime (sessionStorage — cleared when the tab
 *  or the whole browser closes) and stamps the current time as the last
 *  active moment (localStorage — survives across tabs/windows, since
 *  inactivity is a per-USER, not per-tab, concept). */
function markActive(userId: string): void {
  try {
    window.sessionStorage.setItem(sessionSeenKey(userId), '1')
    window.localStorage.setItem(lastActiveKey(userId), String(Date.now()))
  } catch {
    // ignore -- private mode / storage disabled
  }
}

/** Items 1 and 4: true when this is either a genuinely fresh visit (a
 *  brand-new tab/window, or the browser was fully closed and reopened --
 *  sessionStorage is cleared in both cases but survives an in-tab
 *  refresh, F5) or the user has been away for 30+ minutes even within
 *  the same still-open tab. Checked as a single Date.now() snapshot at
 *  specific trigger points (mount, scope change, panel-open) rather
 *  than a live ticking timer, so a laptop sleeping for hours can never
 *  be misread as "only a few seconds of inactivity" the way a timer
 *  that keeps counting through sleep would. */
function isFreshVisit(userId: string): boolean {
  try {
    if (!window.sessionStorage.getItem(sessionSeenKey(userId))) return true
    const lastRaw = window.localStorage.getItem(lastActiveKey(userId))
    const last = lastRaw ? Number(lastRaw) : Number.NaN
    if (!Number.isFinite(last)) return true
    return Date.now() - last > INACTIVITY_RESET_MS
  } catch {
    // Storage unavailable -- can't tell, so don't force a reset on top
    // of an already-degraded experience.
    return false
  }
}

// WP1B: a click on a confirm_card / form action must never surface a
// user-visible chat bubble. The technical envelope
// ([CONFIRM_SUBMIT]/[FORM_SUBMIT] + JSON) that ConfirmCardBlockView /
// FormBlockView post back into the conversation is for the assistant
// only. It should already be redacted down to nothing server-side once
// persisted, but this is the single place that decides what actually
// renders, so it also defends against: the brief pre-refetch optimistic
// bubble, any already-stored history row from before that redaction
// existed, and a now-empty leftover turn.
const _HIDDEN_SUBMISSION_RE = /^\[(CONFIRM|FORM)_SUBMIT\b/
const _HIDDEN_SUBMISSION_TRAILER_RE =
  /^\(Selected ".*" on confirmation card ".*"\.\)$|^\(Submitted form ".*"\.\)$/

// WP: password-relay bugfix -- see the two usages below for the full
// story. Mirrors server/chatbot.py's _STORED_PASSWORD_PLACEHOLDER_RE.
const _REDACTED_PLACEHOLDER_RE = /⟦redacted(?:-password)?⟧/

/** True when the stored history already holds this pending message: same
 *  role and text, saved no earlier than 2 minutes before it was sent (the
 *  margin covers a client clock that runs ahead of the server). */
export function isAlreadyStored(
  pending: { role: string; content: string; created_at?: string | null },
  stored: { role: string; content: string; created_at?: string | null }[],
): boolean {
  const sentAt = pending.created_at ? Date.parse(pending.created_at) : Number.NaN
  return stored.some((m) => {
    if (m.role !== pending.role || m.content.trim() !== pending.content.trim()) return false
    const savedAt = m.created_at ? Date.parse(m.created_at) : Number.NaN
    if (Number.isNaN(sentAt) || Number.isNaN(savedAt)) return false
    return savedAt >= sentAt - 120_000
  })
}

function isHiddenSubmission(m: { role: string; content: string }): boolean {
  if (m.role !== 'user') return false
  const trimmed = m.content.trim()
  if (trimmed.length === 0) return true
  return _HIDDEN_SUBMISSION_RE.test(trimmed) || _HIDDEN_SUBMISSION_TRAILER_RE.test(trimmed)
}

function getOrCreateConvId(pageContext: ChatPageContext, userId: string): string {
  const key = storageKeyFor(pageContext, userId)
  try {
    if (isFreshVisit(userId)) {
      // Items 1/4: a fresh visit or a long-idle return never resumes
      // whatever was last stored for this scope -- start clean instead
      // of silently surfacing an old (possibly unanswered, days-old)
      // conversation. Only the CURRENTLY-viewed scope's stored id is
      // replaced; other projects' stored ids are untouched until (and
      // unless) their own scope is opened, at which point this same
      // check runs again for them.
      const fresh = uuidv4()
      window.sessionStorage.setItem(key, fresh)
      markActive(userId)
      return fresh
    }
    // Item 5 ("ensure separate browser tabs do not overwrite each
    // other's active conversation"): sessionStorage, not localStorage
    // -- each tab gets its own copy, so switching conversations in one
    // tab can never silently change what a DIFFERENT tab resumes into
    // on its own next refresh. It still survives a plain refresh (F5)
    // of THIS tab, which is exactly the persistence item 1 asks for.
    let id = window.sessionStorage.getItem(key)
    if (!id) {
      id = uuidv4()
      window.sessionStorage.setItem(key, id)
    }
    markActive(userId)
    return id
  } catch {
    return uuidv4()
  }
}

// WP1B PROJ-UPD: a lone `confirm_card` block is a lightweight chat
// confirmation (Save/Edit/Discard, Accept as project status/Keep as
// reported) — not a visual sala. It rides along on the assistant's
// pending bubble as `card` so ChatSidebar can render it inline, instead
// of going through the sala dispatcher (which would navigate the whole
// app to a composed-view page and auto-collapse the chat — right for a
// photo grid or a chart, wrong for a short conversational confirmation).
export interface ConfirmCardData {
  id: string
  text: string
  actions: { value: string; label: string }[]
  payload?: Record<string, unknown>
}

export type PendingChatMessage = ChatMessage

export interface ChatConversation {
  conversationId: string
  pageContext: ChatPageContext
  /** Server-confirmed history + pending bubbles (user+assistant pairs
   *  not yet acknowledged by the next history refetch). */
  messages: PendingChatMessage[]
  /** Live streaming text for the in-flight assistant turn. `null` when
   *  no turn is in flight, `''` while the model is "thinking" (no token
   *  has arrived yet), or accumulated text once tokens flow. */
  streamingText: string | null
  sending: boolean
  historyQ: UseQueryResult<ChatHistoryResponse>
  /** `displayText` (WP1B), when given, is what shows in the user's own
   *  bubble; `text` is still what's actually sent to the assistant. Lets
   *  a confirm_card show "Selected \"Save\"." instead of the raw
   *  [CONFIRM_SUBMIT] envelope the model needs to see. */
  sendMessage: (text: string, displayText?: string) => Promise<void>
  newConversation: () => void
  /** WP1B: the confirm_card attached to the LATEST assistant turn, if
   *  any. Deliberately NOT stored on the message object itself — history
   *  refetch clears `pendingMessages` almost immediately (the server
   *  strips the marker before persisting, so the refetched copy has no
   *  card data), which would wipe a message-attached card before it's
   *  even visible. This lives in its own state so it survives that
   *  refetch and is only replaced/cleared by the NEXT turn. */
  // Contractor-update workflow fix: an array so a composed reply
  // reporting several distinct updates at once can show several
  // cards stacked on the one bubble, not just the first/only one.
  pendingCard: ConfirmCardData[] | null
  /** Zonoo: Assistant Conversation Behavior -- a ⟦VIEW⟧ marker parsed off
   *  the LATEST assistant turn, held back rather than auto-dispatched
   *  (see the state-declaration comment in useChatConversation for why).
   *  `null` when the latest turn had no marker, or once a newer turn's
   *  outcome has superseded it. Pass to openPendingViewIntent() to
   *  actually navigate/paint it. */
  pendingViewIntent: ViewIntent | null
  /** Dispatches pendingViewIntent (if any) through the onViewIntent
   *  callback AppShell supplied. A no-op when there is nothing pending,
   *  or when the hook was constructed without an onViewIntent (tests). */
  openPendingViewIntent: () => void
  /** Zonoo: Assistant Conversation Behavior -- the sidebar's unsent
   *  draft, scoped by project (not by conversationId) and persisted to
   *  sessionStorage so it survives the panel closing/reopening and an
   *  in-tab refresh. */
  draft: string
  setDraft: (text: string) => void
  /** True when the stored conversation's last message is an unanswered
   *  user turn AND the server confirms nothing is still actually
   *  running for it (server/chatbot.py's reply_status) -- a genuine
   *  interruption (client disconnect before the assistant's reply was
   *  ever persisted), not just "still waiting" or "still running". */
  isInterrupted: boolean
  /** True when the server reports this conversation's last question is
   *  STILL being processed right now (reply_status === 'running') --
   *  e.g. a different tab sent it, or this tab reconnected while the
   *  original request was still in flight. Retry is refused (both here
   *  and, redundantly, server-side) while this is true, to avoid a
   *  duplicate paid call / duplicate tool execution. */
  isReplyRunning: boolean
  /** Resends the last (unanswered) user message as a new turn. Only
   *  meaningful when isInterrupted is true; a no-op while
   *  isReplyRunning is true. */
  retryLastMessage: () => Promise<void>
  /** Call when the sidebar panel transitions from closed to open --
   *  re-runs the item-4 inactivity check even if the tab was never
   *  reloaded and the project scope never changed. */
  notePanelOpened: () => void
  /** True once the user has explicitly opened a past conversation from
   *  History -- distinct from simply being on an old thread that hasn't
   *  had a message in a while. Drives the "you're viewing an older
   *  conversation" indicator; cleared by starting a new chat or by any
   *  of the normal fresh-visit/scope-change transitions. */
  isHistoricalView: boolean
  /** Opens a specific past conversation (from the History list) as the
   *  active one for the current project scope. */
  openConversation: (conversationId: string) => void
}

export interface UseChatConversationOpts {
  /** AppShell wires the router-aware view-intent dispatcher; tests
   *  pass a spy. When omitted, view intents parsed out of replies are
   *  silently dropped (the chat still works as plain text). */
  onViewIntent?: (intent: ViewIntent) => void
}

export function useChatConversation(opts: UseChatConversationOpts = {}): ChatConversation {
  //@usedby:AppShell.tsx,ChatSidebar.tsx,useChatConversation.test.tsx
  const { onViewIntent } = opts
  const pageContext = useChatPageContext()
  const { user } = useAuth()
  const userId = user?.id ?? 'anon'
  const [conversationId, setConversationId] = useState<string>(() =>
    getOrCreateConvId(pageContext, userId),
  )
  // Mirrors `conversationId` for use inside async callbacks (streamChat's
  // handlers) that were set up for a PREVIOUS conversation and may still
  // resolve after the user has since switched scope or started a new
  // chat. Guarding state updates against this ref (rather than the
  // `conversationId` captured in the closure at call time) is what keeps
  // a late response from bleeding into whatever conversation is CURRENTLY
  // on screen.
  const conversationIdRef = useRef(conversationId)
  useEffect(() => {
    conversationIdRef.current = conversationId
  }, [conversationId])

  const [pendingMessages, setPendingMessages] = useState<PendingChatMessage[]>([])
  // WP1B: see the ChatConversation.pendingCard doc comment — kept
  // separate from pendingMessages so it isn't wiped by the history
  // refetch that runs right after every turn.
  const [pendingCard, setPendingCard] = useState<ConfirmCardData[] | null>(null)
  // Zonoo: Assistant Conversation Behavior -- a parsed ⟦VIEW⟧ marker used
  // to dispatch (navigate away / paint over the current screen) the
  // instant a reply finished streaming, with no warning. That surprised
  // users when the reply already gave a complete text answer and the
  // view was just repeating the same information -- so now it rides
  // along on the pending bubble as an offer (a "See evidence" button,
  // same idea as pendingCard just above) and is only actually dispatched
  // via openPendingViewIntent() when the user chooses to open it.
  const [pendingViewIntent, setPendingViewIntent] = useState<ViewIntent | null>(null)
  const [streamingText, setStreamingText] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const queryClient = useQueryClient()

  const draftKey = draftKeyFor(pageContext, userId)
  const [draft, setDraftState] = useState<string>(() => readDraft(draftKey))
  const setDraft = useCallback(
    (text: string) => {
      setDraftState(text)
      writeDraft(draftKey, text)
    },
    [draftKey],
  )
  // Each project scope keeps its own independent draft -- re-read it
  // when the scope changes instead of carrying one project's unsent
  // text over into another's input box.
  useEffect(() => {
    setDraftState(readDraft(draftKey))
  }, [draftKey])

  const historyQ = useQuery(chatHistoryQuery(conversationId))
  // 30 Sep 2026 (Costa 3): cards are shown on the latest assistant turn
  // only, so answering one of two proposal cards ("demolition finished",
  // "false ceiling next, then plumbing") made the second card vanish
  // unanswered. The cards currently on screen are remembered here so the
  // ones not yet answered are carried onto the next reply.
  const shownCardsRef = useRef<ConfirmCardData[] | null>(null)
  const storedMessages = historyQ.data?.messages ?? []
  const lastStored = storedMessages[storedMessages.length - 1]
  shownCardsRef.current =
    pendingCard ??
    (lastStored && lastStored.role === 'assistant'
      ? (lastStored.confirm_card ?? null)
      : null)
  // 22 Oct 2026 (Dori, Mar 17): the question showed twice while Zoe was still
  // answering. The server saves the question at once; a history refetch (e.g.
  // on window focus) then brought the saved copy while the local pending copy
  // was still shown. A pending message already in the stored history is not
  // shown again.
  const visiblePending = pendingMessages.filter((p) => !isAlreadyStored(p, storedMessages))
  const messages = [...storedMessages, ...visiblePending].filter(
    (m) => !isHiddenSubmission(m),
  )

  // Item 4/6: prefer the server's authoritative reply_status
  // ("running" / "interrupted" / "done") when present -- it knows
  // whether _run_chat_sync's worker thread is genuinely still executing
  // server-side, which a client can never determine on its own (a
  // disconnect only tells you THIS tab lost the stream, not whether the
  // request itself is still running). Fall back to the older
  // awaiting_reply heuristic for any response that doesn't send
  // reply_status yet (existing test mocks). Either way, gated on
  // nothing being in flight LOCALLY too, so the normal few-hundred-ms
  // gap between sending a message and its reply streaming back doesn't
  // flash the Retry affordance on every single turn.
  const notLocallyInFlight = !sending && pendingMessages.length === 0
  const replyStatus = historyQ.data?.reply_status
  const isReplyRunning = replyStatus === 'running' && notLocallyInFlight
  const isInterrupted = replyStatus
    ? replyStatus === 'interrupted' && notLocallyInFlight
    : Boolean(historyQ.data?.awaiting_reply) && notLocallyInFlight

  const sendMessage = useCallback(
    async (text: string, displayText?: string) => {
      const requestConvId = conversationId
      setSending(true)
      const userMessage: PendingChatMessage = {
        role: 'user',
        content: displayText ?? text,
        created_at: new Date().toISOString(),
      }
      markActive(userId)
      setPendingMessages((prev) => [...prev, userMessage])
      setStreamingText('')

      // Contractor-update workflow fix: read off the OUTGOING envelope,
      // not the reply -- a click on "Save" or "Accept as project
      // status" posts this back as a [CONFIRM_SUBMIT] envelope (see
      // ConfirmCardBlockView's buildConfirmSubmitMessage). A plain
      // string check is enough here; the envelope's JSON is only ever
      // built by that one function, never typed by the admin.
      const isMutatingConfirmAction =
        /\[CONFIRM_SUBMIT/.test(text) && /"action":"(save|accept)"/.test(text)
      const submittedCardId = /\[CONFIRM_SUBMIT id="([^"]+)"\]/.exec(text)?.[1]
      const carriedCards = submittedCardId
        ? (shownCardsRef.current ?? []).filter((c) => c.id !== submittedCardId)
        : []

      let accumulated = ''
      await streamChat(
        { message: text, conversationId: requestConvId, pageContext },
        {
          onToken: (chunk) => {
            accumulated += chunk
            // Cross-conversation bleed fix: only touch the LIVE
            // streaming bubble when this token still belongs to the
            // conversation currently on screen.
            if (conversationIdRef.current === requestConvId) {
              setStreamingText(accumulated)
            }
          },
          onDone: () => {
            // CDV-001: split the view-intent marker out of the streamed
            // text before it reaches the bubble. Server-side the
            // persisted transcript already has it stripped; this client
            // strip handles the pending bubble before the refetch lands.
            const { clean, intent, error } = extractViewIntent(accumulated)
            if (error) {
              console.warn('[viewIntent] dropped:', error, accumulated)
            }
            // WP1B: a composed reply whose ONLY content is confirm_card
            // block(s) is a chat-native confirmation, not a sala —
            // attach it to the bubble instead of dispatching to the
            // view-intent router (which would navigate away and, per
            // CDV-016, auto-collapse this very panel).
            //
            // Contractor-update workflow fix: this used to require
            // EXACTLY one block, so reporting two distinct updates in
            // one message (the model proposing two confirm_cards in one
            // composed reply) fell through to the view-intent router
            // instead — dispatched as a full-page "Composed view" sala
            // with both cards rendered full-width, instead of showing
            // inline in the chat like a single-update proposal does.
            // Relaxed to "one or more blocks, ALL of them confirm_card"
            // — still never dispatched as a sala, just multiple cards
            // stacked on the one bubble.
            let cards: ConfirmCardData[] | null = null
            let dispatchIntent = intent
            const allConfirmCardBlocks =
              intent && intent.kind === 'composed' && intent.blocks.length > 0 &&
              intent.blocks.every((b) => b.type === 'confirm_card')
                ? intent.blocks
                : undefined
            if (allConfirmCardBlocks) {
              cards = allConfirmCardBlocks.map((b) => ({
                id: b.id,
                text: b.text,
                actions: b.actions,
                payload: b.payload,
              }))
              dispatchIntent = null
            }
            // WP: password-relay bugfix. server/mcp_server.py wraps a
            // one-time secret (e.g. a freshly generated password) in
            // ⟦PW⟧...⟦/PW⟧ markers so server/chatbot.py's storage layer
            // can find and redact it before persisting. Nothing upstream
            // of this point strips those markers back out for display,
            // so unwrap them into plain text here -- same idea as the
            // ⟦VIEW⟧ marker strip just above.
            const displayClean = clean.replace(/⟦PW⟧([\s\S]*?)⟦\/PW⟧/g, '$1')
            // Contractor-update workflow fix: the Save/Edit/Discard
            // proposal card's OWN "text" already carries the full
            // interpretation ("I understood that pool work is
            // paused..."), per the system prompt's instruction to emit
            // it "on its own, with nothing ... in the reply text before
            // the view marker". That instruction is prompt-only, so
            // enforce it defensively here too: when the card is
            // specifically that proposal card (identified by its
            // "save" action, distinct from the later Accept/Keep-as-
            // reported card), never show a preamble bubble alongside
            // it, even if the model wrote one. The Accept/Keep card's
            // own short confirmation ("Saved as reported project
            // information.", "Accepted as the project's current
            // status.") is intentionally kept -- that's the "short
            // confirmation and the existing choice" the workflow is
            // supposed to show, not a duplicated interpretation.
            // Contractor-update workflow fix: with multiple cards on
            // one bubble, treat it as the proposal-card case only when
            // EVERY card is a proposal card (has a "save" action) — a
            // batch of two "Save this update?" cards from one message
            // reporting two updates, never a mix with an Accept/Keep
            // card, which always arrives on its own turn.
            const isProposalCard =
              cards !== null && cards.length > 0 &&
              cards.every((c) => c.actions.some((a) => a.value === 'save'))
            const assistantMessage: PendingChatMessage = {
              role: 'assistant',
              content: isProposalCard ? '' : displayClean || t('Empty response.'),
              created_at: new Date().toISOString(),
            }
            // Cross-conversation bleed fix: this turn's OWN conversation
            // (requestConvId) always gets its history cache refreshed a
            // few lines below regardless of what's on screen right now
            // -- so switching back to it later shows the real answer.
            // But the shared, not-per-conversation pending/streaming/
            // sending state below must only be touched when this
            // response still belongs to whatever conversation the user
            // is CURRENTLY looking at.
            const stillCurrent = conversationIdRef.current === requestConvId
            if (stillCurrent) {
              setPendingMessages((prev) => [...prev, assistantMessage])
              setStreamingText(null)
              // Replaces whatever card was pending from the PREVIOUS turn
              // (null clears it) — this turn's outcome is authoritative.
              const newIds = new Set((cards ?? []).map((c) => c.id))
              const merged = [...(cards ?? []), ...carriedCards.filter((c) => !newIds.has(c.id))]
              setPendingCard(merged.length > 0 ? merged : null)
              setPendingViewIntent(dispatchIntent)
            }
            // Contractor-update workflow fix: saving or accepting a
            // Project Update from chat changes what the site dashboard
            // shows (the "From the contractor" line, the Planning
            // note) -- but the dashboard's own query only refetches on
            // its own ~30s poll tick, or never on a date-pinned view
            // where polling is off. Detected from the raw envelope this
            // turn was SENT with (captured in the outer closure, not
            // the reply), so it fires whether or not this conversation
            // is still the one on screen -- the dashboard should catch
            // up regardless of what the admin looked at next.
            if (isMutatingConfirmAction && pageContext.scope !== 'portfolio') {
              queryClient.invalidateQueries({ queryKey: queryKeys.site(pageContext.site_id) })
            }
            // WP: password-relay bug, real root cause. This used to be
            // `queryClient.invalidateQueries(...).then(...)` (and, before
            // that, `.fetchQuery(...)`) -- but both of those respect
            // React Query's staleTime bookkeeping, and can resolve
            // without ever making a real network request when the cache
            // entry is not (yet) considered stale, silently handing back
            // whatever was already cached instead of what the server
            // actually has right now. A plain, uncached fetch is the
            // only way to reliably know. This matters here because the
            // server intentionally persists a REDACTED copy of any reply
            // that carried a one-time secret (e.g. a freshly generated
            // password) -- correct for long-term storage, but blindly
            // trusting a "refetch" that may not have actually happened
            // clears the live (correct, unredacted) bubble as soon as
            // the real background refetch eventually lands -- typically
            // under a second after the admin's only chance to see the
            // real value appeared. Only drop the locally-shown bubble
            // when the server's persisted copy of THIS turn's assistant
            // reply actually matches what was shown; otherwise keep
            // showing the live version, since that's the one that's
            // real.
            apiFetch(
              `/api/chat/history?conversation_id=${encodeURIComponent(requestConvId)}`,
              ChatHistoryResponse,
            )
              .then((latest) => {
                const lastIdx = latest.messages.length - 1
                const lastServerMessage = latest.messages[lastIdx]
                // Narrow and specific on purpose: only step in when the
                // persisted copy carries the redaction placeholder that
                // the locally-shown copy does NOT have. A broader "any
                // content mismatch" check also fires for confirm_card-
                // only turns (the server strips that marker differently
                // than the client does), which would misfire here too.
                const serverWasRedacted =
                  lastServerMessage?.role === 'assistant' &&
                  _REDACTED_PLACEHOLDER_RE.test(lastServerMessage.content) &&
                  !_REDACTED_PLACEHOLDER_RE.test(assistantMessage.content)
                // Patch the redacted copy in the fetched data with what
                // was actually shown, THEN write it into the query cache
                // (what historyQ / useQuery reads) and always clear the
                // pending bubble -- avoids ever showing both the pending
                // and history copies of the same turn at once. This
                // write is keyed by requestConvId, not by whatever the
                // user is looking at right now, so it's always correct.
                const patched = serverWasRedacted
                  ? {
                      ...latest,
                      messages: latest.messages.map((m, i) =>
                        i === lastIdx ? { ...m, content: assistantMessage.content } : m,
                      ),
                    }
                  : latest
                queryClient.setQueryData(queryKeys.chatHistory(requestConvId), patched)
                if (stillCurrent) setPendingMessages([])
              })
              .catch(() => {
                // If the refetch itself fails, fall back to clearing the
                // pending bubble rather than leaving a duplicate forever
                // -- but only for the conversation this turn belongs to.
                if (stillCurrent) setPendingMessages([])
              })
            if (stillCurrent) setSending(false)
          },
          onError: (msg) => {
            if (conversationIdRef.current !== requestConvId) return
            const failure: ChatMessage = {
              role: 'assistant',
              content: msg || t('The assistant is unavailable. Please try again.'),
              created_at: new Date().toISOString(),
            }
            setPendingMessages((prev) => [...prev, failure])
            setStreamingText(null)
            setSending(false)
          },
        },
      )
    },
    [conversationId, pageContext, queryClient, userId],
  )

  const retryLastMessage = useCallback(async () => {
    // Item 4: refuse client-side too (the server refuses independently
    // via _INFLIGHT_REQUESTS) -- a request for this conversation is
    // still genuinely running, so resending now would risk a duplicate
    // paid call / duplicate tool execution rather than a real retry.
    if (isReplyRunning) return
    const lastUser = [...messages].reverse().find((m) => m.role === 'user')
    if (!lastUser) return
    await sendMessage(lastUser.content)
  }, [messages, sendMessage, isReplyRunning])

  const [isHistoricalView, setIsHistoricalView] = useState(false)

  const newConversation = useCallback(() => {
    const fresh = uuidv4()
    try {
      window.sessionStorage.setItem(storageKeyFor(pageContext, userId), fresh)
    } catch {
      // ignore — private mode
    }
    markActive(userId)
    setIsHistoricalView(false)
    setConversationId(fresh)
  }, [pageContext, userId])

  const openConversation = useCallback(
    (id: string) => {
      try {
        window.sessionStorage.setItem(storageKeyFor(pageContext, userId), id)
      } catch {
        // ignore — private mode
      }
      markActive(userId)
      setIsHistoricalView(true)
      setConversationId(id)
    },
    [pageContext, userId],
  )

  const notePanelOpened = useCallback(() => {
    // Item 4: re-check inactivity at the moment the panel is opened, not
    // just at mount/scope-change -- covers "the tab was open the whole
    // time but the panel itself was closed for 45 minutes."
    if (isFreshVisit(userId)) {
      setIsHistoricalView(false)
      setConversationId(getOrCreateConvId(pageContext, userId))
    } else {
      markActive(userId)
    }
  }, [pageContext, userId])

  // Re-derive the conversation id when the project in view changes (e.g.
  // navigating from one site's dashboard to another's). Without this the
  // id computed at mount would stick around and two different sites
  // would end up sharing one message history.
  //
  // Item 1 ("switching projects or moving to/from All projects: show a
  // fresh welcome... returning to a previously visited project: do not
  // automatically reopen its old conversation"): the FIRST time this
  // effect runs (mount / page load, including a plain refresh) it
  // should still respect the item-4 freshness check via
  // getOrCreateConvId, so refreshing the SAME scope correctly resumes.
  // Every SUBSEQUENT run means the scope actually changed while the app
  // was already open -- an explicit in-app navigation to a different
  // project or All projects -- and per item 1 that always starts fresh,
  // regardless of how recently (even seconds ago, well inside the
  // 30-minute window) this or any other scope was active. Without this
  // distinction, switching projects during an active session would
  // silently resume whatever was last stored for the destination --
  // exactly the bug this whole work package exists to fix, just
  // triggered by a project switch instead of pure inactivity.
  const isInitialScopeRender = useRef(true)
  const scopeKey = storageKeyFor(pageContext, userId)
  // biome-ignore lint/correctness/useExhaustiveDependencies: only the scope key matters
  useEffect(() => {
    setIsHistoricalView(false)
    if (isInitialScopeRender.current) {
      isInitialScopeRender.current = false
      setConversationId(getOrCreateConvId(pageContext, userId))
      return
    }
    const fresh = uuidv4()
    try {
      window.sessionStorage.setItem(scopeKey, fresh)
    } catch {
      // ignore -- private mode / storage disabled
    }
    markActive(userId)
    setConversationId(fresh)
  }, [scopeKey])

  const openPendingViewIntent = useCallback(() => {
    if (pendingViewIntent && onViewIntent) {
      onViewIntent(pendingViewIntent)
    }
  }, [pendingViewIntent, onViewIntent])

  // Reset pending/streaming state on conversation change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only the id reset matters
  useEffect(() => {
    setPendingMessages([])
    setStreamingText(null)
    setSending(false)
    setPendingCard(null)
    setPendingViewIntent(null)
  }, [conversationId])

  return {
    conversationId,
    pageContext,
    messages,
    streamingText,
    sending,
    historyQ,
    sendMessage,
    newConversation,
    pendingCard,
    pendingViewIntent,
    openPendingViewIntent,
    draft,
    setDraft,
    isInterrupted,
    isReplyRunning,
    retryLastMessage,
    notePanelOpened,
    isHistoricalView,
    openConversation,
  }
}

const ChatConversationCtx = createContext<ChatConversation | null>(null)

export function ChatConversationProvider({
  //@usedby:AppShell.tsx,useChatConversation.test.tsx
  value,
  children,
}: {
  value: ChatConversation
  children: ReactNode
}) {
  return createElement(ChatConversationCtx.Provider, { value }, children)
}

export function useChatConversationContext(): ChatConversation {
  //@usedby:ChatSidebar.tsx,FormBlockView.tsx
  const v = useContext(ChatConversationCtx)
  if (!v) {
    throw new Error(
      'useChatConversationContext: no <ChatConversationProvider> in the tree. ' +
        'Mount one in AppShell, or wrap your test render with one.',
    )
  }
  return v
}

/** 29 Oct 2026: the same conversation, or null outside the app shell (Zoe's
 *  card on the site page falls back to a plain card then). */
export function useOptionalChatConversation(): ChatConversation | null {
  return useContext(ChatConversationCtx)
}
